/**
 * Notifications Store
 * Управление уведомлениями: список, счётчик непрочитанных, настройки каналов
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ChannelPreference, Notification, TypePreference } from '../types/notifications.types'
import { notificationsApi } from '../api/notifications.api'

const POLL_INTERVAL = 30_000 // 30 секунд

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(20)
  const isLoading = ref(false)
  const preferences = ref<ChannelPreference[]>([])
  /** Настройки по типам событий (DEV-767): только сохранённые записи; нет в списке = включён. */
  const eventPreferences = ref<TypePreference[]>([])
  const isPanelOpen = ref(false)

  let pollTimer: ReturnType<typeof setInterval> | null = null

  // --- Actions ---

  async function fetchUnreadCount() {
    try {
      unreadCount.value = await notificationsApi.getUnreadCount()
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }

  async function fetchNotifications(p = 1) {
    isLoading.value = true
    try {
      const result = await notificationsApi.listNotifications(p, pageSize.value)
      if (p === 1) {
        notifications.value = result.notifications
      } else {
        notifications.value.push(...result.notifications)
      }
      total.value = result.total
      page.value = result.page
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore() {
    if (notifications.value.length >= total.value) return
    await fetchNotifications(page.value + 1)
  }

  async function markRead(notificationId: string) {
    try {
      await notificationsApi.markRead(notificationId)
      const notif = notifications.value.find((n) => n.id === notificationId)
      if (notif && !notif.isRead) {
        notif.isRead = true
        notif.readAt = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (error) {
      console.error('Failed to mark notification read:', error)
    }
  }

  async function markAllRead() {
    try {
      const count = await notificationsApi.markAllRead()
      notifications.value.forEach((n) => {
        if (!n.isRead) {
          n.isRead = true
          n.readAt = new Date().toISOString()
        }
      })
      unreadCount.value = 0
      return count
    } catch (error) {
      console.error('Failed to mark all read:', error)
      return 0
    }
  }

  async function fetchPreferences() {
    try {
      preferences.value = await notificationsApi.getPreferences()
    } catch (error) {
      console.error('Failed to fetch preferences:', error)
    }
  }

  async function updatePreferences(prefs: ChannelPreference[]) {
    try {
      await notificationsApi.updatePreferences(prefs)
      preferences.value = prefs
    } catch (error) {
      console.error('Failed to update preferences:', error)
      throw error
    }
  }

  async function fetchEventPreferences() {
    try {
      eventPreferences.value = await notificationsApi.getEventPreferences()
    } catch (error) {
      console.error('Failed to fetch event preferences:', error)
    }
  }

  /** Upsert настроек типов (DEV-767): локально мержим по notificationType. */
  async function updateEventPreferences(prefs: TypePreference[]) {
    try {
      await notificationsApi.updateEventPreferences(prefs)
      const merged = new Map(eventPreferences.value.map((p) => [p.notificationType, p]))
      prefs.forEach((p) => merged.set(p.notificationType, p))
      eventPreferences.value = [...merged.values()]
    } catch (error) {
      console.error('Failed to update event preferences:', error)
      throw error
    }
  }

  function togglePanel() {
    isPanelOpen.value = !isPanelOpen.value
    if (isPanelOpen.value && notifications.value.length === 0) {
      fetchNotifications()
    }
  }

  function closePanel() {
    isPanelOpen.value = false
  }

  function startPolling() {
    if (pollTimer) return
    fetchUnreadCount()
    pollTimer = setInterval(fetchUnreadCount, POLL_INTERVAL)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  return {
    // State
    notifications,
    unreadCount,
    total,
    page,
    pageSize,
    isLoading,
    preferences,
    isPanelOpen,

    // Actions
    fetchUnreadCount,
    fetchNotifications,
    loadMore,
    markRead,
    markAllRead,
    fetchPreferences,
    updatePreferences,
    eventPreferences,
    fetchEventPreferences,
    updateEventPreferences,
    togglePanel,
    closePanel,
    startPolling,
    stopPolling,
  }
})
